import { useRef, useEffect, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { Vector3 } from 'three'
import useAppStore from '../stores/appStore'

/**
 * CameraController - Smoothly transitions camera based on chapter selection
 * Works WITH OrbitControls by temporarily taking over during transitions,
 * then handing control back to OrbitControls.
 *
 * Overview mode: Camera at [0, 3, 25] looking at origin
 * Chapter mode: Camera at [0, 2, 14] centered on poem content for full visibility
 */
export function CameraController({ orbitControlsRef }) {
  const { camera } = useThree()
  const { currentChapter } = useAppStore()

  // Target positions
  const targetPosition = useRef(new Vector3(0, 3, 25))
  const targetLookAt = useRef(new Vector3(0, 0, 0))

  // Transition state
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionProgress = useRef(0)
  const startPosition = useRef(new Vector3())
  const previousChapter = useRef(currentChapter)

  // Transition duration and speed
  const TRANSITION_SPEED = 4 // Higher = faster transitions

  // Update target based on currentChapter
  useEffect(() => {
    // Only trigger transition if chapter actually changed
    if (previousChapter.current !== currentChapter) {
      // Start transition
      setIsTransitioning(true)
      transitionProgress.current = 0
      startPosition.current.copy(camera.position)

      // Disable OrbitControls during transition
      if (orbitControlsRef?.current) {
        orbitControlsRef.current.enabled = false
      }

      if (currentChapter === null) {
        // Overview mode - pull back to see all chapters
        // Y=3 gives good angle with polar constraints
        targetPosition.current.set(0, 3, 25)
        targetLookAt.current.set(0, 0, 0)
      } else {
        // Single chapter mode - centered view for full poem visibility
        // Y=2 allows viewing from slightly above
        targetPosition.current.set(0, 2, 14)
        targetLookAt.current.set(0, 0, 0)
      }

      previousChapter.current = currentChapter
    }
  }, [currentChapter, camera, orbitControlsRef])

  // Smooth camera transitions using eased lerp
  useFrame((state, delta) => {
    if (!isTransitioning) return

    // Update transition progress
    transitionProgress.current += delta * TRANSITION_SPEED

    // Use smooth easing function (ease-out cubic)
    const t = Math.min(transitionProgress.current, 1)
    const eased = 1 - Math.pow(1 - t, 3)

    // Interpolate camera position
    camera.position.lerpVectors(startPosition.current, targetPosition.current, eased)

    // Always look at target during transition
    camera.lookAt(targetLookAt.current)

    // Check if transition is complete
    if (t >= 1) {
      setIsTransitioning(false)

      // Re-enable OrbitControls and update its target
      if (orbitControlsRef?.current) {
        orbitControlsRef.current.target.copy(targetLookAt.current)
        orbitControlsRef.current.enabled = true
        orbitControlsRef.current.update()
      }
    }
  })

  return null
}

export default CameraController
